// packages/charts/src/scatterSeries.ts
//
// The scatter breakdown's series rules, in a leaf module: the builder
// (`optionBuilder.ts`) and the series colours (`seriesColors.ts`) both read
// them, and the builder imports `seriesColors.ts`, so neither may import the
// other for these (ADR-0476; step 4 review M1).

/**
 * The most series a scatter with a breakdown is split into: the registry's
 * `BREAKDOWN_SERIES_CAP` (`api/constants/visualizations.py`), the number the
 * bar and line breakdowns are capped at by the backend pivot. A scatter is
 * not pivoted — an "Other" over two metric axes would be a point at summed
 * coordinates — so the cap is applied here, by DROPPING the smallest series
 * and saying how many (`scatterSeriesNote`).
 */
export const SCATTER_MAX_SERIES = 8;
/** A NULL or empty breakdown value: its own series, never a dropped point —
 *  the same name the backend pivot gives it (`chart_formatter._NOT_SET_LABEL`). */
export const SCATTER_NOT_SET_LABEL = "(not set)";
/** Fixed and muted, so "(not set)" never takes a palette colour a real value
 *  would otherwise keep. */
export const SCATTER_NOT_SET_COLOR = "#868e96";

/**
 * Numbers numerically and before text; text by code point. A numeric-looking
 * STRING stays text (the warehouse said string, and "10" before "9" is then
 * the honest order) — the rule the pivot's column order uses (plan 2026-10-16
 * seasonal heatmap, D2), so a year keeps its colour across refreshes.
 */
export function compareSeriesValues(a: unknown, b: unknown): number {
  const aNum = typeof a === "number" && Number.isFinite(a);
  const bNum = typeof b === "number" && Number.isFinite(b);
  if (aNum && bNum) return (a as number) - (b as number);
  if (aNum) return -1;
  if (bNum) return 1;
  const as = String(a);
  const bs = String(b);
  return as < bs ? -1 : as > bs ? 1 : 0;
}

/**
 * The value as a number, or `null` when there is no measurement here.
 *
 * `Number.isFinite(Number(v))` is NOT enough, and the gap is exactly the
 * dangerous one: `Number(null)`, `Number("")`, `Number(false)` and
 * `Number([])` are all `0` — a perfectly finite number — so a finiteness
 * guard admits every empty value as a genuine zero. `undefined` and `"n/a"`
 * give `NaN` and would have been caught; `null` is the one a warehouse
 * actually sends for a metric that has no value, and it is the one that
 * slipped through. Found by a test, not by reading the code.
 *
 * Distinct from `toNum`, which exists to make a hole DRAWABLE (as zero) and is
 * right for a bar chart. This one exists to make a hole VISIBLE to the caller,
 * so a plot with two metric axes can decline to place the row at all.
 *
 * Here rather than in the option builder because the scatter's placement
 * below reads it, and the series colours must place exactly the same rows.
 */
export const measuredNumber = (value: any): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

/** One placed row: its breakdown value beside whatever the caller built. */
export type PlacedScatterRow<T> = { raw: unknown; item: T };

/**
 * The rows a scatter can PLACE: both metric axes measured. A row whose x or y
 * is not a finite number is left out rather than drawn at the origin (see
 * `buildScatterFamilyOption`). `toItem` builds what the caller draws for the
 * row; the series colours pass a no-op and read only the breakdown values.
 */
export function placeScatterRows<T>(
  rows: Record<string, any>[],
  yKeys: readonly string[],
  breakdownKey: string | undefined,
  toItem: (row: Record<string, any>, index: number, x: number, y: number) => T,
): PlacedScatterRow<T>[] {
  const [xMetric, yMetric] = yKeys;
  if (xMetric == null || yMetric == null) return [];
  return rows.flatMap((row, i) => {
    const xValue = measuredNumber(row[xMetric]);
    const yValue = measuredNumber(row[yMetric]);
    if (xValue === null || yValue === null) return [];
    return [{ raw: breakdownKey ? row[breakdownKey] : undefined, item: toItem(row, i, xValue, yValue) }];
  });
}

/** One series of a scatter with a breakdown, before it has a colour. */
export type ScatterSeriesGroup<T> = { name: string; notSet: boolean; items: T[] };

/**
 * Placed rows grouped into one series per breakdown value: sorted by the value
 * (numbers first), "(not set)" last. Over `SCATTER_MAX_SERIES` the series with
 * the FEWEST points are dropped — never summed — and counted in `dropped`.
 *
 * The ONE grouping: the builder draws these groups and `seriesRefsOf` lists
 * them, so the Format pane can never offer a series the plot dropped, nor miss
 * one it drew (plan 2026-10-18 chart colours, step 6).
 */
export function groupScatterSeries<T>(placed: readonly PlacedScatterRow<T>[]): {
  groups: ScatterSeriesGroup<T>[];
  dropped: number;
} {
  const byName = new Map<string, { raw: unknown; notSet: boolean; items: T[] }>();
  for (const { raw, item } of placed) {
    const notSet = raw === null || raw === undefined || raw === "";
    const name = notSet ? SCATTER_NOT_SET_LABEL : String(raw);
    const group = byName.get(name) ?? { raw, notSet, items: [] };
    group.items.push(item);
    byName.set(name, group);
  }
  const ordered = [...byName.entries()].sort(([, a], [, b]) =>
    a.notSet !== b.notSet ? (a.notSet ? 1 : -1) : compareSeriesValues(a.raw, b.raw),
  );
  // Rank by point count; equal counts keep the sorted order (a stable sort).
  const kept = new Set(
    [...ordered]
      .sort(([, a], [, b]) => b.items.length - a.items.length)
      .slice(0, SCATTER_MAX_SERIES)
      .map(([name]) => name),
  );
  const groups = ordered
    .filter(([name]) => kept.has(name))
    .map(([name, g]) => ({ name, notSet: g.notSet, items: g.items }));
  return { groups, dropped: ordered.length - groups.length };
}
